import React, { useState, useEffect, useCallback } from 'react';
import { useConversation } from '../../hooks/useConversation';
import ConversationView from './ConversationView';
import MessagesDisplay from './MessagesDisplay';
import './VelvypalInbox.css';

// Simple relative time formatter
const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHr / 24);

    if (diffSec < 60) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  } catch {
    return '';
  }
};

/**
 * Full-screen inbox modal with New/Conversations tabs
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {Function} props.onClose - Handler to close modal
 * @param {Object} props.user - Firebase user object
 * @param {Array} props.connections - All user connections
 * @param {Array} props.newMessages - New incoming messages (no reply yet)
 * @param {Array} props.conversations - Ongoing conversations
 * @param {number} props.freeMessages - Daily free messages
 * @param {number} props.purchasedMessages - Purchased messages
 * @param {boolean} props.loading - Whether connections are loading
 * @param {Function} props.onRefresh - Handler to refresh connections
 * @param {Function} props.onBuyMessages - Handler to open buy messages modal
 * @param {Function} props.refreshMessages - Handler to refresh message count
 */
export default function VelvypalInbox({
  isOpen,
  onClose,
  user,
  connections = [],
  newMessages = [],
  conversations = [],
  freeMessages = 0,
  purchasedMessages = 0,
  loading = false,
  onRefresh,
  onBuyMessages,
  refreshMessages,
}) {
  const [activeTab, setActiveTab] = useState('conversations');
  const [selectedConnection, setSelectedConnection] = useState(null);

  // Conversation hook for selected connection
  const {
    messages,
    connection: conversationConnection,
    loading: messagesLoading,
    sending,
    send: sendMessage,
    markRead,
  } = useConversation(selectedConnection?.id, user);

  // Mark messages as read when opening a conversation
  useEffect(() => {
    if (selectedConnection && user) {
      markRead();
    }
  }, [selectedConnection, user, markRead]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedConnection(null);
      // Switch to new tab if there are new messages
      if (newMessages.length > 0 && conversations.length === 0) {
        setActiveTab('new');
      } else {
        setActiveTab('conversations');
      }
    }
  }, [isOpen, newMessages.length, conversations.length]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (selectedConnection) {
          setSelectedConnection(null);
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, selectedConnection, onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      if (selectedConnection) {
        setSelectedConnection(null);
      } else {
        onClose();
      }
    }
  };

  const handleSendMessage = useCallback(async (content) => {
    const result = await sendMessage(content);
    if (result.success) {
      refreshMessages?.();
    }
    return result;
  }, [sendMessage, refreshMessages]);

  const formatTime = formatRelativeTime;

  const truncateMessage = (text, maxLength = 50) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength).trim() + '...';
  };

  if (!isOpen) return null;

  const totalMessages = freeMessages + purchasedMessages;

  // If a conversation is selected, show the conversation view
  if (selectedConnection) {
    return (
      <div className="velvypal-inbox-overlay" onClick={handleOverlayClick}>
        <div className="velvypal-inbox-modal conversation-mode">
          <ConversationView
            messages={messages}
            connection={conversationConnection}
            otherHandle={selectedConnection.other_handle}
            loading={messagesLoading}
            sending={sending}
            totalMessages={totalMessages}
            onSend={handleSendMessage}
            onBack={() => {
              setSelectedConnection(null);
              onRefresh?.();
            }}
            onBuyMessages={onBuyMessages}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="velvypal-inbox-overlay" onClick={handleOverlayClick}>
      <div className="velvypal-inbox-modal">
        {/* Header */}
        <div className="inbox-header">
          <h2>Your Letters</h2>
          <div className="inbox-header-right">
            <MessagesDisplay
              freeMessages={freeMessages}
              purchasedMessages={purchasedMessages}
              onClick={onBuyMessages}
              variant="compact"
            />
            <button className="inbox-close" onClick={onClose} aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="inbox-tabs">
          <button
            className={`inbox-tab ${activeTab === 'new' ? 'active' : ''}`}
            onClick={() => setActiveTab('new')}
          >
            New
            {newMessages.length > 0 && (
              <span className="tab-badge">{newMessages.length}</span>
            )}
          </button>
          <button
            className={`inbox-tab ${activeTab === 'conversations' ? 'active' : ''}`}
            onClick={() => setActiveTab('conversations')}
          >
            Conversations
          </button>
        </div>

        {/* Content */}
        <div className="inbox-content">
          {loading ? (
            <div className="inbox-loading">
              <div className="loading-spinner"></div>
              <span>Loading...</span>
            </div>
          ) : activeTab === 'new' ? (
            newMessages.length === 0 ? (
              <div className="inbox-empty">
                <span className="empty-icon">📬</span>
                <span className="empty-text">No new messages</span>
                <span className="empty-hint">When someone sends you a Velvynote, you'll see it here</span>
              </div>
            ) : (
              <div className="inbox-list">
                {newMessages.map((msg) => (
                  <button
                    key={msg.id}
                    className="inbox-convo-item"
                    onClick={() => setSelectedConnection(msg)}
                  >
                    <div className="convo-avatar new">
                      {msg.other_handle?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div className="convo-info">
                      <div className="convo-header">
                        <span className="convo-handle">@{msg.other_handle}</span>
                        <span className="convo-new-badge">New</span>
                        <span className="convo-time">{formatTime(msg.created_at)}</span>
                      </div>
                      <p className="convo-preview">{truncateMessage(msg.last_message, 100)}</p>
                    </div>
                    {msg.unread_count > 0 && (
                      <span className="convo-unread">{msg.unread_count}</span>
                    )}
                  </button>
                ))}
              </div>
            )
          ) : (
            conversations.length === 0 ? (
              <div className="inbox-empty">
                <span className="empty-icon">💌</span>
                <span className="empty-text">No conversations yet</span>
                <span className="empty-hint">Click "Send Velvynote" on someone's memory to start</span>
              </div>
            ) : (
              <div className="inbox-list">
                {conversations.map((convo) => (
                  <button
                    key={convo.id}
                    className="inbox-convo-item"
                    onClick={() => setSelectedConnection(convo)}
                  >
                    <div className="convo-avatar">
                      {convo.other_handle?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div className="convo-info">
                      <div className="convo-header">
                        <span className="convo-handle">@{convo.other_handle}</span>
                        <span className="convo-time">{formatTime(convo.last_message_at || convo.last_activity)}</span>
                      </div>
                      <p className="convo-preview">{truncateMessage(convo.last_message)}</p>
                    </div>
                    {convo.unread_count > 0 && (
                      <span className="convo-unread">{convo.unread_count}</span>
                    )}
                  </button>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
